import { useEffect } from 'react';
import { Filters, TFilterItem, Divider, Pagination } from '@treezor/design-system';

import { useTranslation } from 'react-i18next';

import { useKycDocumentList } from '@/features/kyc/hooks/useKycDocumentList';
import { useKycDocumentFilter } from '@/features/kyc/hooks/useKycDocumentFilter';
import { KycDocumentListLoader } from '@/features/kyc/components/kyc-documents/KycDocumentListLoader';
import { KycDocumentListItem } from '@/features/kyc/components/kyc-documents/KycDocumentListItem';
import { KYC_DOCUMENTS_PER_PAGE } from '@/features/kyc/constants';
import { EmptyState } from '@/ui-kit/templates/EmptyState/EmptyState';

export function KycDocumentList() {
  const { isLoading, documents } = useKycDocumentList();

  const { t } = useTranslation();
  const {
    statusOptions,
    filterStatus,
    handleFilterStatus,
    typeOptions,
    filterType,
    handleFilterType,
    rows,
    activePage,
    hasPagination,
    documentCount,
    handlePageChange,
    hasFilter,
    onResetFilter,
  } = useKycDocumentFilter({
    documents,
  });

  const items: TFilterItem[] = [
    {
      id: 'filterStatus',
      label: t('pages.currentProfiles.kyc.global.labels.kycStatus'),
      type: 'LIST',
      items: statusOptions.map(({ label }) => label),
      selectedValues: filterStatus,
      onValuesChange: handleFilterStatus,
      onReset: () => handleFilterStatus(undefined),
    },
    {
      id: 'filterType',
      label: t('pages.currentProfiles.kyc.documents.preview.forms.documentType'),
      type: 'LIST',
      items: typeOptions.map(({ label }) => label),
      selectedValues: filterType,
      onValuesChange: handleFilterType,
      onReset: () => handleFilterType(undefined),
    },
  ];

  useEffect(() => {
    if (documents?.length > 0 && hasFilter) {
      onResetFilter();
      handlePageChange(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documents?.length]);

  if (documents?.length === 0) {
    return isLoading ? (
      <KycDocumentListLoader />
    ) : (
      <EmptyState title={t('pages.currentProfiles.kyc.documents.empty')} icon="document_v1" size="m" className="p-9" />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <Filters
        labels={{
          filterButton: t('pages.cores.filter'),
          selectedValues: '',
          clearButton: t('pages.cores.clearFilters'),
        }}
        items={items}
        defaultFilters={['filterStatus', 'filterType']}
        onClearFilters={onResetFilter}
      />
      <div className="flex flex-col gap-3.5 py-3.5 px-3.5 border-2 border-primary rounded-200 bg-primary w-full">
        {rows.map((document, index) => (
          <div className="flex flex-col gap-3.5" key={`item-${document.documentId}`}>
            <KycDocumentListItem document={document} isLoading={isLoading} />
            {index !== rows.length - 1 && <Divider />}
          </div>
        ))}
      </div>
      {hasPagination && (
        <Pagination
          isRangeVisible
          itemCount={documentCount}
          activePage={activePage}
          onPageChange={(item) => handlePageChange(item.selected)}
          itemPerPage={KYC_DOCUMENTS_PER_PAGE}
          isFirstButtonVisible
          isLastButtonVisible
          className="flex justify-end"
        />
      )}
    </div>
  );
}
